import React, { useEffect, useState } from "react";
import InnerLayout from "../../components/layouts/inner-layout";
import { useDispatch } from "react-redux";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import errorToast from "../../components/helper-components/error-toast";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend
} from "recharts";
import BadgesComponent from "../../components/tables/badges-component";
import { DollarSign, Users, Building2, CreditCard } from 'lucide-react';
import { dashboardMultiAction } from '../../redux/actions/dashboard-action';
import { format, differenceInCalendarDays, parseISO, isValid } from 'date-fns';

const CATEGORY_COLORS = ["#377DFF", "#22C55E", "#A259FF", "#FFB200", "#FF6B6B", "#4ECDC4", "#45B29D", "#F7CA18"];

// Custom Tooltip for Sales Trend
const SalesTrendTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        // Format date label from YYYY-MM-DD format
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        let dateLabel = data.range;
        if (dateLabel && dateLabel.includes("-")) {
            const parts = dateLabel.split("-");
            if (parts.length === 3) {
                const [year, month, day] = parts;
                dateLabel = `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
            }
        }
        return (
            <div style={{ background: '#fff', border: '1px solid var(--dashboard-gray-200)', borderRadius: 10, padding: 14, minWidth: 140, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ color: 'var(--dashboard-gray-500)', fontSize: 13, marginBottom: 4 }}>{dateLabel}</div>
                <div style={{ color: 'var(--dashboard-gray-900)', fontWeight: 700, fontSize: 18 }}>₹{data.total_sales.toLocaleString()}</div>
                <div style={{ color: 'var(--dashboard-gray-400)', fontSize: 12, marginTop: 2 }}>Total Sales</div>
            </div>
        );
    }
    return null;
};

const NewDashboard = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [periodParams, setPeriodParams] = useState({ period: 'yearly', fromDate: '', toDate: '' });

    useEffect(() => {
        setLoading(true);
        dispatch(dashboardMultiAction(
            {
                period: periodParams.period,
                donut_chart_filter: 'bySales',
                ...(periodParams.period === 'custom_date' && periodParams.fromDate && periodParams.toDate
                    ? { from: periodParams.fromDate, to: periodParams.toDate }
                    : {})
            },
            (res) => {
                if (res.status) {
                    setDashboardData(res.data);
                } else {
                    errorToast(res.message || "Failed to fetch dashboard data");
                }
                setLoading(false);
            },
            (err) => {
                errorToast("Failed to fetch dashboard data");
                setLoading(false);
            }
        ));
    }, [dispatch, periodParams]);

    // Helper to get comparison label based on period
    const getComparisonLabel = (period) => {
        switch (period) {
            case 'daily': return 'vs yesterday';
            case 'weekly': return 'vs last week';
            case 'monthly': return 'vs last month';
            case 'quarterly': return 'vs last quarter';
            case 'yearly': return 'vs last year';
            case 'custom_date': return 'vs previous period';
            default: return 'vs previous';
        }
    };

    return (
        <InnerLayout
            title="Dashboard"
            showDashboardFilters={true}
            onPeriodChange={setPeriodParams}
        >
            {(loading || !dashboardData) ? (
                <div className="p-8 animate-pulse">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className=" bg-gradient-to-br h-24 rounded-lg from-[var(--dashboard-gray-100)] to-gray-200" />
                        ))}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mb-6">
                        <div className="bg-gray-200 h-[500px] rounded-xl lg:col-span-4" />
                        <div className="bg-gray-200 h-[500px] rounded-xl lg:col-span-2" />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="bg-gray-200 h-64 rounded-xl" />
                        ))}
                    </div>
                </div>
            ) : (
                <>

                    {/* Quick Stats Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 px-6 pt-6">
                        {/* Total Sales */}
                        <div className="bg-white rounded-lg border border-[var(--dashboard-gray-200)] p-4 flex justify-between">
                            <div>
                                <h3 style={{ color: 'var(--dashboard-gray-500)', fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Total Sales</h3>
                                <div style={{ color: 'var(--dashboard-gray-900)', fontSize: 24, fontWeight: 700, marginBottom: 16 }}>₹{dashboardData.total_sales?.toLocaleString() ?? 0}</div>
                                <div className="flex items-center" style={{ gap: 4 }}>
                                    <span style={{ color: dashboardData.sales_trend_direction === 'positive' ? 'var(--dashboard-success)' : dashboardData.sales_trend_direction === 'negative' ? 'var(--dashboard-danger)' : 'var(--dashboard-warning)', fontWeight: 600, fontSize: 14 }}>
                                        {dashboardData.sales_trend_direction === 'positive' ? '↑' : dashboardData.sales_trend_direction === 'negative' ? '↓' : '—'} {dashboardData.sales_percentage_change ?? 0}%
                                    </span>
                                    <span style={{ color: 'var(--dashboard-gray-500)', fontSize: 13, marginLeft: 3 }}>{getComparisonLabel(periodParams.period)}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--dashboard-bg-info)' }}>
                                <DollarSign size={18} color="var(--dashboard-icon-info)" />
                            </div>
                        </div>
                        {/* Total Customers */}
                        <div className="bg-white rounded-lg  p-4 border border-[var(--dashboard-gray-200)] flex justify-between">
                            <div>
                                <h3 style={{ color: 'var(--dashboard-gray-500)', fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Total Customers</h3>
                                <div style={{ color: 'var(--dashboard-gray-900)', fontSize: 24, fontWeight: 700, marginBottom: 16 }}>{dashboardData.total_customers?.toLocaleString() ?? 0}</div>
                                <div className="flex items-center" style={{ gap: 4 }}>
                                    <span style={{ color: dashboardData.customers_trend_direction === 'positive' ? 'var(--dashboard-success)' : dashboardData.customers_trend_direction === 'negative' ? 'var(--dashboard-danger)' : 'var(--dashboard-warning)', fontWeight: 600, fontSize: 14 }}>
                                        {dashboardData.customers_trend_direction === 'positive' ? '↑' : dashboardData.customers_trend_direction === 'negative' ? '↓' : '—'} {dashboardData.customers_percentage_change ?? 0}%
                                    </span>
                                    <span style={{ color: 'var(--dashboard-gray-500)', fontSize: 13, marginLeft: 3 }}>{getComparisonLabel(periodParams.period)}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--dashboard-bg-purple)' }}>
                                <Users size={18} color="var(--dashboard-icon-purple)" />
                            </div>
                        </div>
                        {/* Avg. Sale Value */}
                        <div className="bg-white rounded-lg  p-4 border border-[var(--dashboard-gray-200)] flex justify-between">
                            <div>
                                <h3 style={{ color: 'var(--dashboard-gray-500)', fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Avg. Sale Value</h3>
                                <div style={{ color: 'var(--dashboard-gray-900)', fontSize: 24, fontWeight: 700, marginBottom: 16 }}>{dashboardData.AvgSaleValue?.avg_sale_value?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? 0}</div>
                                <div className="flex items-center" style={{ gap: 4 }}>
                                    <span style={{ color: dashboardData.AvgSaleValue?.avg_sale_value_trend_direction === 'positive' ? 'var(--dashboard-success)' : dashboardData.AvgSaleValue?.avg_sale_value_trend_direction === 'negative' ? 'var(--dashboard-danger)' : 'var(--dashboard-warning)', fontWeight: 600, fontSize: 14 }}>
                                        {dashboardData.AvgSaleValue?.avg_sale_value_trend_direction === 'positive' ? '↑' : dashboardData.AvgSaleValue?.avg_sale_value_trend_direction === 'negative' ? '↓' : '—'} {dashboardData.AvgSaleValue?.avg_sale_value_percentage_change ?? 0}%
                                    </span>
                                    <span style={{ color: 'var(--dashboard-gray-500)', fontSize: 13, marginLeft: 3 }}>{getComparisonLabel(periodParams.period)}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--dashboard-bg-success)' }}>
                                <Building2 size={18} color="var(--dashboard-icon-success)" />
                            </div>
                        </div>
                        {/* Pending Payments */}
                        <div className="bg-white rounded-lg  p-4 border border-[var(--dashboard-gray-200)] flex justify-between">
                            <div>
                                <h3 style={{ color: 'var(--dashboard-gray-500)', fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Pending Payments</h3>
                                <div style={{ color: 'var(--dashboard-gray-900)', fontSize: 24, fontWeight: 700, marginBottom: 16 }}>₹{dashboardData.payment_in_pending?.toLocaleString() ?? 0}</div>
                                <div className="flex items-center" style={{ gap: 4 }}>
                                    <span style={{ color: dashboardData.payment_in_pending_trend_direction === 'positive' ? 'var(--dashboard-success)' : dashboardData.payment_in_pending_trend_direction === 'negative' ? 'var(--dashboard-danger)' : 'var(--dashboard-warning)', fontWeight: 600, fontSize: 14 }}>
                                        {dashboardData.payment_in_pending_trend_direction === 'positive' ? '↑' : dashboardData.payment_in_pending_trend_direction === 'negative' ? '↓' : '—'} {dashboardData.payment_in_pending_percentage_change ?? 0}%
                                    </span>
                                    <span style={{ color: 'var(--dashboard-gray-500)', fontSize: 13, marginLeft: 3 }}>{getComparisonLabel(periodParams.period)}</span>
                                </div>
                            </div>
                            <div className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--dashboard-bg-warning)' }}>
                                <CreditCard size={18} color="var(--dashboard-icon-warning)" />
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mb-6 px-6">
                        <div className="bg-white rounded-xl p-6 h-[500px] flex flex-col justify-between border border-[var(--dashboard-gray-200)] lg:col-span-4">
                            <div>
                                <h2 className="text-lg font-bold text-[var(--dashboard-gray-900)] mb-1">Sales Trend</h2>
                                <p className="text-[var(--dashboard-gray-400)] text-sm mb-6">Monthly revenue by region</p>
                            </div>
                            {/* Only horizontal scroll, fixed height */}
                            {(() => {
                                const sales = dashboardData.salesTrend.map(d => d.total_sales);
                                let min = Math.min(...sales, 0);
                                let max = Math.max(...sales, 0);
                                if (min === max) {
                                    min = 0;
                                    max = max === 0 ? 1000 : max * 1.2;
                                } else {
                                    const pad = (max - min) * 0.1;
                                    min = Math.floor(min - pad);
                                    max = Math.ceil(max + pad);
                                }

                                // Determine appropriate formatting based on max value
                                const formatYAxisValue = (value) => {
                                    if (max >= 1000000) {
                                        return `₹${(value / 1000000).toFixed(1)}M`;
                                    } else if (max >= 1000) {
                                        return `₹${(value / 1000).toFixed(1)}k`;
                                    } else {
                                        return `₹${value.toFixed(0)}`;
                                    }
                                };

                                return (
                                    <div className="flex-1 min-h-0 overflow-x-auto scrollbar-rounded pb-4" style={{ height: 360 }}>
                                        <div style={{ minWidth: Math.max(900, (dashboardData.salesTrend?.length || 12) * 80), height: 345 }}>
                                            <ResponsiveContainer width="100%">
                                                <LineChart
                                                    data={dashboardData.salesTrend}
                                                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 0" vertical={false} stroke="var(--dashboard-gray-200)" />
                                                    <XAxis
                                                        dataKey="range"
                                                        tickFormatter={dateStr => {
                                                            // Handle YYYY-MM-DD format from API
                                                            if (dateStr && dateStr.includes("-")) {
                                                                const parts = dateStr.split("-");
                                                                if (parts.length === 3) {
                                                                    const [year, month, day] = parts;
                                                                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                                    const monthName = months[parseInt(month, 10) - 1];
                                                                    return `${day} ${monthName}`;
                                                                }
                                                            }
                                                            return dateStr;
                                                        }}
                                                        tick={{ fill: 'var(--dashboard-gray-500)', fontSize: 14 }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickMargin={12}
                                                    />
                                                    <YAxis
                                                        domain={[min, max]}
                                                        tickFormatter={formatYAxisValue}
                                                        tick={{ fill: 'var(--dashboard-gray-500)', fontSize: 14 }}
                                                        axisLine={false}
                                                        tickLine={false}
                                                        tickMargin={16}
                                                    />
                                                    <Tooltip content={<SalesTrendTooltip />} cursor={{ fill: 'rgba(55,125,255,0.08)' }} />
                                                    <Line
                                                        type="linear"
                                                        dataKey="total_sales"
                                                        stroke="var(--dashboard-blue)"
                                                        strokeWidth={5}
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        dot={false}
                                                        activeDot={{ r: 7, stroke: 'var(--dashboard-blue)', strokeWidth: 2, fill: '#fff' }}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="bg-white rounded-xl p-6 h-[500px] flex flex-col border border-[var(--dashboard-gray-200)] lg:col-span-2">
                            <h2 className="text-lg font-bold text-[var(--dashboard-gray-900)] mb-1">Item Category</h2>
                            <p className="text-[var(--dashboard-gray-400)] text-sm mb-6">Sales by Item Category</p>
                            <div className="flex-1 flex flex-col items-center justify-center">
                                {(() => {
                                    // Ensure percentage is a number
                                    const catData = (dashboardData.donutChart.category || []).map(cat => ({
                                        ...cat,
                                        percentage: typeof cat.percentage === 'string' ? parseFloat(cat.percentage) : cat.percentage
                                    }));
                                    return (
                                        <PieChart width={200} height={200}>
                                            <Pie
                                                data={catData}
                                                dataKey="percentage"
                                                nameKey="dim_name"
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={80}
                                                outerRadius={90}
                                                startAngle={90}
                                                endAngle={-270}
                                                paddingAngle={0}
                                                cornerRadius={0}
                                            >
                                                {catData.map((entry, idx) => (
                                                    <Cell key={`cell-${idx}`} fill={CATEGORY_COLORS[idx % CATEGORY_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            {/* Center label */}
                                            <text
                                                x="50%"
                                                y="48%"
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fontSize="24px"
                                                fontWeight="bold"
                                                fill="#222"
                                            >
                                                ₹1.45M
                                            </text>
                                            <text
                                                x="50%"
                                                y="58%"
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fontSize="12px"
                                                fill="var(--dashboard-gray-500)"
                                            >
                                                Total Sales
                                            </text>
                                        </PieChart>
                                    );
                                })()}
                                {/* Custom Legend */}
                                <div className="mt-6 w-full flex flex-col gap-2 min-h-28">
                                    {(dashboardData.donutChart.category || [])
                                        .filter(cat => parseFloat(cat.percentage) > 0)
                                        .map((cat, idx) => (
                                            <div key={cat.dim_name} className="flex items-center justify-between text-[var(--dashboard-gray-700)] text-base">
                                                <div className="flex items-center gap-2">
                                                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: CATEGORY_COLORS[idx % CATEGORY_COLORS.length] }}></span>
                                                    <span className="text-[var(--dashboard-gray-600)] text-sm">{cat.dim_name}</span>
                                                </div>
                                                <div className="">
                                                    <span className="text-[var(--dashboard-gray-400)] text-sm">{cat.percentage}%</span>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Products Tables Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 px-6">
                        {/* Top Products by Revenue */}
                        <div className="bg-white rounded-xl p-6 border border-[var(--dashboard-gray-200)]">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-lg font-bold text-[var(--dashboard-gray-900)] mb-2">Top Products</h2>
                                    <p className="text-[var(--dashboard-gray-400)] text-sm">Best performing products by <span className="font-semibold text-[var(--dashboard-gray-700)]">Revenue</span></p>
                                </div>
                                <BadgesComponent status="sales" />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-[var(--dashboard-gray-50)] text-[var(--dashboard-gray-500)] text-xs">
                                            <th className="text-left font-medium px-4 py-3">Product</th>
                                            <th className="text-left font-medium px-4 py-3">Category</th>
                                            <th className="text-right font-medium px-4 py-3">Qty Sold</th>
                                            <th className="text-right font-medium px-4 py-3">Sale Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dashboardData.topProductsBySales || []).slice(0, 6).map((prod, idx) => (
                                            <tr key={prod.item_id} className="border-b last:border-b-0 border-[var(--dashboard-gray-200)]">
                                                <td className="px-4 py-4">
                                                    <div
                                                        className="font-medium text-[var(--dashboard-gray-900)] truncate max-w-[180px] cursor-pointer"
                                                        title={prod.item_name}
                                                    >
                                                        {prod.item_name}
                                                    </div>
                                                    <div className="text-xs text-[var(--dashboard-gray-400)]">{prod.item_code}</div>
                                                </td>
                                                <td className="px-4 py-3">{prod.category_name || '--'}</td>
                                                <td className="px-4 py-3 text-right font-semibold">{prod.net_sold_quantity}</td>
                                                <td className="px-4 py-3 text-right font-semibold">₹{parseFloat(prod.net_sales_amount).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* Top Products by Quantity */}
                        <div className="bg-white rounded-xl p-6 border border-[var(--dashboard-gray-200)]">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-lg font-bold text-[var(--dashboard-gray-900)] mb-2">Top Products</h2>
                                    <p className="text-[var(--dashboard-gray-400)] text-sm">Best performing products by <span className="font-semibold text-[var(--dashboard-gray-700)]">Quantity</span></p>
                                </div>
                                <BadgesComponent status="sales" />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-[var(--dashboard-gray-50)] text-[var(--dashboard-gray-500)] text-xs">
                                            <th className="text-left font-medium px-4 py-3">Product</th>
                                            <th className="text-left font-medium px-4 py-3">Category</th>
                                            <th className="text-right font-medium px-4 py-3">Sale Value</th>
                                            <th className="text-right font-medium px-4 py-3">Qty Sold</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dashboardData.topProductsBySalesQuantity || []).slice(0, 6).map((prod, idx) => (
                                            <tr key={prod.item_id} className="border-b last:border-b-0 border-[var(--dashboard-gray-200)]">
                                                <td className="px-4 py-4">
                                                    <div
                                                        className="font-medium text-[var(--dashboard-gray-900)] truncate max-w-[180px] cursor-pointer"
                                                        title={prod.item_name}
                                                    >
                                                        {prod.item_name}
                                                    </div>
                                                    <div className="text-xs text-[var(--dashboard-gray-400)]">{prod.item_code}</div>
                                                </td>
                                                <td className="px-4 py-4">{prod.category_name || '--'}</td>
                                                <td className="px-4 py-4 text-right font-semibold">₹{parseFloat(prod.net_sales_amount).toLocaleString()}</td>
                                                <td className="px-4 py-4 text-right font-semibold">{prod.net_sold_quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Top Products by Purchase Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 px-6">
                        {/* Top Products by Purchase Value */}
                        <div className="bg-white rounded-xl p-6 border border-[var(--dashboard-gray-200)]">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-lg font-bold text-[var(--dashboard-gray-900)] mb-2">Top Products</h2>
                                    <p className="text-[var(--dashboard-gray-400)] text-sm">Top purchased products by <span className="font-semibold text-[var(--dashboard-gray-700)]">Purchase</span></p>
                                </div>
                                <BadgesComponent status="purchase" />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-[var(--dashboard-gray-50)] text-[var(--dashboard-gray-500)] text-xs">
                                            <th className="text-left font-medium px-4 py-3">Product</th>
                                            <th className="text-left font-medium px-4 py-3">Category</th>
                                            <th className="text-right font-medium px-4 py-3">Qty Purchased</th>
                                            <th className="text-right font-medium px-4 py-3">Purchase Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dashboardData.topProductsByPurchase || []).slice(0, 6).map((prod, idx) => (
                                            <tr key={prod.item_id} className="border-b last:border-b-0 border-[var(--dashboard-gray-200)]">
                                                <td className="px-4 py-4">
                                                    <div
                                                        className="font-medium text-[var(--dashboard-gray-900)] truncate max-w-[180px] cursor-pointer"
                                                        title={prod.item_name}
                                                    >
                                                        {prod.item_name}
                                                    </div>
                                                    <div className="text-xs text-[var(--dashboard-gray-400)]">{prod.item_code}</div>
                                                </td>
                                                <td className="px-4 py-3">{prod.category_name || '--'}</td>
                                                <td className="px-4 py-3 text-right font-semibold">{prod.net_purchase_sold_quantity}</td>
                                                <td className="px-4 py-3 text-right font-semibold">₹{parseFloat(prod.net_purchase_amount).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* Top Products by Purchase Quantity */}
                        <div className="bg-white rounded-xl p-6 border border-[var(--dashboard-gray-200)]">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-lg font-bold text-[var(--dashboard-gray-900)] mb-2">Top Products</h2>
                                    <p className="text-[var(--dashboard-gray-400)] text-sm">Top purchased products by <span className="font-semibold text-[var(--dashboard-gray-700)]">Quantity</span></p>
                                </div>
                                <BadgesComponent status="purchase" />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr className="bg-[var(--dashboard-gray-50)] text-[var(--dashboard-gray-500)] text-xs">
                                            <th className="text-left font-medium px-4 py-3">Product</th>
                                            <th className="text-left font-medium px-4 py-3">Category</th>
                                            <th className="text-right font-medium px-4 py-3">Purchase Value</th>
                                            <th className="text-right font-medium px-4 py-3">Qty Purchased</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(dashboardData.topProductsByPurchaseQuantity || []).slice(0, 6).map((prod, idx) => (
                                            <tr key={prod.item_id} className="border-b last:border-b-0 border-[var(--dashboard-gray-200)]">
                                                <td className="px-4 py-4">
                                                    <div
                                                        className="font-medium text-[var(--dashboard-gray-900)] truncate max-w-[180px] cursor-pointer"
                                                        title={prod.item_name}
                                                    >
                                                        {prod.item_name}
                                                    </div>
                                                    <div className="text-xs text-[var(--dashboard-gray-400)]">{prod.item_code}</div>
                                                </td>
                                                <td className="px-4 py-4">{prod.category_name || '--'}</td>
                                                <td className="px-4 py-4 text-right font-semibold">₹{parseFloat(prod.net_purchase_amount).toLocaleString()}</td>
                                                <td className="px-4 py-4 text-right font-semibold">{prod.net_purchase_sold_quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Payment IN Status and Top Customers Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 px-6">
                        {/* Payment IN Status */}
                        <div className="bg-white rounded-xl p-6 border border-[var(--dashboard-gray-200)] flex flex-col justify-between">
                            <h2 className="text-lg font-bold text-[var(--dashboard-gray-900)] mb-2">Payment IN Status</h2>
                            <p className="text-[var(--dashboard-gray-400)] text-sm mb-6">Overview of receivables</p>
                            <div className="flex flex-col md:flex-row gap-6">
                                {/* Payment Aging */}
                                <div className="flex-1 min-w-[220px]">
                                    <h4 className="text-base font-semibold mb-3">Payment Aging</h4>
                                    {(dashboardData.paymentStatusRangeChart?.data || []).filter(d => d.current_date || d[Object.keys(d)[1]] !== undefined).map((aging, idx) => {
                                        // Determine label and value
                                        let label = '';
                                        let value = 0;
                                        if (aging.current_date) {
                                            label = 'Current';
                                            value = aging.paid_amount;
                                        } else {
                                            const key = Object.keys(aging).find(k => k !== 'need_to_pay_amount');
                                            label = key ? key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';
                                            value = aging.need_to_pay_amount;
                                        }
                                        // Color logic (example, you can adjust)
                                        const colors = ['var(--dashboard-success)', 'var(--dashboard-info)', 'var(--dashboard-warning)', 'var(--dashboard-orange)', 'var(--dashboard-danger)'];
                                        return (
                                            <div key={label} className="mb-4">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span>{label}</span>
                                                    <span className="font-medium">₹{value?.toLocaleString()}</span>
                                                </div>
                                                <div className="w-full h-2 rounded" style={{ background: 'var(--dashboard-border)' }}>
                                                    <div className="h-2 rounded" style={{ width: `${value > 0 ? Math.min(100, value) : 2}%`, background: colors[idx % colors.length] }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div className="flex justify-between text-sm font-semibold mt-5">
                                        <span>Total Receivables</span>
                                        <span>₹{dashboardData.paymentStatusRangeChart?.total_receivables?.toLocaleString() ?? 0}</span>
                                    </div>
                                </div>
                                {/* Upcoming Payments */}
                                <div className="flex-1 min-w-[260px]">
                                    <h4 className="text-base font-semibold mb-3">Upcoming Payments</h4>
                                    {(dashboardData.paymentStatusRangeChart?.upcoming_payments || []).length === 0 && (
                                        <div className="text-[var(--dashboard-gray-400)] text-sm">No upcoming payments</div>
                                    )}

                                    {(dashboardData.paymentStatusRangeChart?.upcoming_payments || []).map((pay, idx) => {
                                        let dueDateStr = '-';
                                        let days = null;
                                        if (pay.poit_due_date) {
                                            const dueDate = parseISO(pay.poit_due_date);
                                            if (isValid(dueDate)) {
                                                dueDateStr = format(dueDate, 'MMM d, yyyy');
                                                days = differenceInCalendarDays(dueDate, new Date());
                                                console.log(days)
                                            }
                                        }
                                        return (
                                            <div key={pay.poit_invoice_number} className="mb-3 p-3 rounded-lg border" style={{ borderColor: 'var(--dashboard-border)' }}>
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium" style={{ color: 'var(--dashboard-gray-900)' }}>{pay.cmr_name}</span>
                                                    <span className="font-semibold">₹{Number(pay.need_to_pay_amount).toLocaleString()}</span>
                                                </div>
                                                <div className="text-xs" style={{ color: 'var(--dashboard-gray-400)', marginBottom: 4 }}> {pay.poit_invoice_number}</div>
                                                <div className="flex justify-between items-center text-xs mt-2">
                                                    <span style={{ color: 'var(--dashboard-gray-500)' }}>Due: {dueDateStr}</span>
                                                    {Number(pay.need_to_pay_amount) > 0 && days !== null && days >= 0 && (() => {
                                                        let bg = 'var(--dashboard-badge-due-later-bg)';
                                                        let color = 'var(--dashboard-badge-due-later-text)';
                                                        if (days <= 7) {
                                                            bg = 'var(--dashboard-badge-due-soon-bg)';
                                                            color = 'var(--dashboard-badge-due-soon-text)';
                                                        }
                                                        return (
                                                            <span
                                                                className="px-4 py-1 rounded-full text-xs font-semibold"
                                                                style={{
                                                                    background: bg,
                                                                    color,
                                                                    display: 'inline-block',
                                                                    minWidth: 90,
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                {`Due in ${days} days`}
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {(dashboardData.paymentStatusRangeChart?.upcoming_payments || []).length != 0 && (
                                        <div className="mt-6 text-center">
                                            <a href="#" style={{ color: 'var(--dashboard-info)' }} className="text-sm font-medium">View All Invoices</a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Top Customers */}
                        <div className="bg-white rounded-xl p-6 border border-[var(--dashboard-gray-200)] flex flex-col ">
                            <div>
                                <h2 className="text-lg mb-2 font-bold" style={{ color: 'var(--dashboard-gray-900)' }}>Top Customers</h2>
                                <p className="text-sm mb-6" style={{ color: 'var(--dashboard-gray-400)' }}>By revenue this quarter</p>
                            </div>
                            <div className="flex flex-col gap-4 overflow-y-auto h-96">
                                {(dashboardData.topCustomers.filter(c => c.total_sales_amount > 0) || []).map((cust, idx) => (
                                    <div key={cust.cmr_id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-base font-semibold" style={{ background: '#E0E7FF', color: '#6366F1' }}>{(cust.cmr_first_name?.[0] || '-') + (cust.cmr_last_name?.[0] || '')}</div>
                                            <div>
                                                <div className="font-medium mb-1" style={{ color: 'var(--dashboard-gray-900)' }}>{cust.cmr_first_name || cust.cmr_last_name ? `${cust.cmr_first_name} ${cust.cmr_last_name}` : '--'}</div>
                                                <div className="text-xs" style={{ color: 'var(--dashboard-gray-400)' }}>₹{parseFloat(cust.total_sales_amount).toLocaleString()} • {cust.order_count} orders</div>
                                            </div>
                                        </div>
                                        <div className="text-sm font-semibold" style={{ color: cust.customer_order_trend_direction === 'positive' ? 'var(--dashboard-success)' : cust.customer_order_trend_direction === 'negative' ? 'var(--dashboard-danger)' : 'var(--dashboard-warning)' }}>
                                            {cust.customer_order_trend_direction === 'positive' ? '+' : cust.customer_order_trend_direction === 'negative' ? '-' : ''}{cust.customer_order_percentage_change}%
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )
            }

        </InnerLayout >
    );
};

export default NewDashboard; 